'use client'

import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'

type AshbyAPI = {
  iFrame: () => { load: () => void }
  registeredMessageHandler: ((event: MessageEvent) => void) | null
}

export default function AshbyBoard({ content }: { content: {
  embedUrl: string; boardUrl: string; externalLabel: string; loading: string; unavailable: string; frameTitle: string
} }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading')

  useEffect(() => {
    const container = containerRef.current
    const timeout = window.setTimeout(() => setStatus(current => current === 'ready' ? current : 'unavailable'), 15000)
    // Ashby announces the board before reporting height when it enters the viewport.
    const receive = (event: MessageEvent) => {
      const frame = container?.querySelector('iframe')
      if (event.origin === 'https://jobs.ashbyhq.com' && event.source === frame?.contentWindow && (typeof event.data === 'number' || event.data === 'reset_jid' || (typeof event.data === 'string' && event.data.startsWith('set_jid=')))) {
        setStatus('ready')
      }
    }
    const observer = new MutationObserver(() => {
      container?.querySelector('iframe')?.setAttribute('title', content.frameTitle)
    })
    if (container) observer.observe(container, { childList: true })
    window.addEventListener('message', receive)
    return () => {
      window.clearTimeout(timeout)
      observer.disconnect()
      window.removeEventListener('message', receive)
      const api = (window as Window & { __Ashby?: AshbyAPI }).__Ashby
      if (api?.registeredMessageHandler) window.removeEventListener('message', api.registeredMessageHandler)
    }
  }, [content.frameTitle])

  const load = () => {
    const api = (window as Window & { __Ashby?: AshbyAPI }).__Ashby
    // onReady also runs when returning through client-side navigation.
    api?.iFrame().load()
  }

  return <div className="fp-ashby-board">
    {status !== 'ready' && <div className="fp-ashby-status" role="status"><p>{status === 'loading' ? content.loading : content.unavailable}</p>
      {status === 'unavailable' && <a className="fp-text-link" href={content.boardUrl} target="_blank" rel="noopener noreferrer">{content.externalLabel} ↗</a>}
    </div>}
    <div id="ashby_embed" ref={containerRef} />
    <Script src={content.embedUrl} strategy="afterInteractive" onReady={load} onError={() => setStatus('unavailable')} />
    <noscript><a href={content.boardUrl}>{content.externalLabel}</a></noscript>
  </div>
}
